/**
 * The one modal behind every "Export" action.
 *
 * It owns only the scope choice and the create call. Each view supplies a
 * `buildPayload` closure because only the view knows its own filters, sort,
 * search and KPI selection — the modal deliberately knows nothing about any
 * particular report's shape.
 */
import { type FC, useCallback, useEffect, useState } from 'react';
import { Modal, Radio } from 'antd';
import type { ButtonProps, RadioChangeEvent } from 'antd';

import { sendNotification } from 'utils/helperMethods';
import { trackTpoGenEvent } from '../utils/trackTpoGenEvent';
import {
    TPO_REPORT_EXPORT_CANCEL_BTN,
    TPO_REPORT_EXPORT_CONFIRM_BTN,
    TPO_REPORT_EXPORT_MODAL,
    TPO_REPORT_EXPORT_SCOPE_GROUP,
    tpoOptionTestId,
} from '../config/testIds';
import { useExportJobs } from './context/ExportJobsContext';
import useCreateReportExportMutation from './hooks/mutations/useCreateReportExportMutation';
import { buildExportEventProps, exportEventName, REPORT_KIND_PAGE_NAMES } from './utils/exportAnalytics';
import { EXPORT_MODAL_LABELS, EXPORT_NOTIFICATIONS, EXPORT_SCOPE_OPTIONS } from './config/exportConstants';
import type { ExportScope, ReportKind } from './types';

const DEFAULT_SCOPE: ExportScope = 'current_view';

export interface ReportExportModalProps {
    open: boolean;
    onClose: () => void;
    /** URL plan id for the create call; the base plan for `plan_comparison`. */
    planId: string;
    reportKind: ReportKind;
    /** Composes the request body for the chosen scope. Memoize at the call site. */
    buildPayload: (scope: ExportScope) => Record<string, unknown>;
    /** `plan_comparison` only: comparison set size, for TPOGEN-004/005/006. */
    planCount?: number;
}

const ReportExportModal: FC<ReportExportModalProps> = ({
    open,
    onClose,
    planId,
    reportKind,
    buildPayload,
    planCount,
}) => {
    const [scope, setScope] = useState<ExportScope>(DEFAULT_SCOPE);

    const { addJob } = useExportJobs();

    const { createExport, isCreating } = useCreateReportExportMutation({
        planId,
        onSuccess: (data, variables) => {
            if (!data?.id) {
                sendNotification('error', EXPORT_NOTIFICATIONS.createFailed);
                return;
            }
            addJob({
                exportId: data.id,
                planId,
                reportKind,
                scope: variables.scope,
                createdAt: Date.now(),
                filename: data.filename ?? undefined,
                planCount,
            });
            sendNotification('info', EXPORT_NOTIFICATIONS.queued);
            onClose();
        },
        onError: (message) => {
            sendNotification('error', message || EXPORT_NOTIFICATIONS.createFailed);
            // A create that never returned a job id has no poller to report
            // it, so the failure trigger is fired here instead.
            trackTpoGenEvent(
                exportEventName(reportKind, 'failed'),
                REPORT_KIND_PAGE_NAMES[reportKind],
                buildExportEventProps(
                    { reportKind, scope, planId, planCount },
                    { durationMs: 0, failureReason: message || EXPORT_NOTIFICATIONS.createFailed },
                ),
            );
        },
    });

    // Every open starts from the default rather than from the last choice: the
    // scope is a per-export decision, not a remembered preference.
    useEffect(() => {
        if (open) setScope(DEFAULT_SCOPE);
    }, [open]);

    const handleScopeChange = useCallback((e: RadioChangeEvent) => setScope(e.target.value as ExportScope), []);

    const handleConfirm = useCallback(() => {
        trackTpoGenEvent(
            exportEventName(reportKind, 'started'),
            REPORT_KIND_PAGE_NAMES[reportKind],
            buildExportEventProps({ reportKind, scope, planId, planCount }),
        );
        createExport({ report_kind: reportKind, payload: buildPayload(scope), scope });
    }, [reportKind, scope, planId, planCount, buildPayload, createExport]);

    return (
        <Modal
            title={EXPORT_MODAL_LABELS.title}
            open={open}
            width={480}
            onCancel={onClose}
            onOk={handleConfirm}
            okText={EXPORT_MODAL_LABELS.confirm}
            cancelText={EXPORT_MODAL_LABELS.cancel}
            confirmLoading={isCreating}
            destroyOnClose
            okButtonProps={{ 'data-testid': TPO_REPORT_EXPORT_CONFIRM_BTN } as ButtonProps}
            cancelButtonProps={{ 'data-testid': TPO_REPORT_EXPORT_CANCEL_BTN } as ButtonProps}
        >
            <div className="flex flex-col gap-y-3" data-testid={TPO_REPORT_EXPORT_MODAL}>
                <p className="text-sm text-[#52525C]">{EXPORT_MODAL_LABELS.subtitle}</p>
                <Radio.Group
                    value={scope}
                    onChange={handleScopeChange}
                    data-testid={TPO_REPORT_EXPORT_SCOPE_GROUP}
                >
                    <div className="flex flex-col gap-y-4">
                        {EXPORT_SCOPE_OPTIONS.map((option) => (
                            <Radio
                                key={option.value}
                                value={option.value}
                                data-testid={tpoOptionTestId(TPO_REPORT_EXPORT_SCOPE_GROUP, option.value)}
                            >
                                <span className="text-sm text-[#18181B]">{option.label}</span>
                                <span className="block text-xs text-[#70707B]">{option.hint}</span>
                            </Radio>
                        ))}
                    </div>
                </Radio.Group>
            </div>
        </Modal>
    );
};

ReportExportModal.displayName = 'ReportExportModal';
export default ReportExportModal;
